import { Cancel, CheckCircle } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback } from 'react';

import { updateUserRequest } from '../../services/userRequest';
import { UserRequestStatus, UserRequestType } from '../../Types/enums';
import { UserRequest } from '../../Types/userRequest';
import { useAuth } from '../Providers/AuthProvider';
import { useNotifications } from '../Providers/NotificationsProvider';
import { statusIconMap } from './UserRequestPanel';

export interface UserRequestConfirmationDialogProps {
  typeLabels: Record<UserRequestType, string>;
  openDialog: boolean;
  handleCloseDialog: () => void;
  selectedRequest: UserRequest | undefined;
  refetchRequests: () => void;
}

export const UserRequestConfirmationDialog = ({
  typeLabels,
  openDialog,
  handleCloseDialog,
  selectedRequest,
  refetchRequests,
}: UserRequestConfirmationDialogProps): JSX.Element => {
  const { refetchNotifications } = useNotifications();
  const { currentUser } = useAuth();
  const renderStatusIcon = (status: UserRequestStatus) => statusIconMap[status] || null;
  const approvedMessage =
    selectedRequest?.type === UserRequestType.DEEP_RESEARCH_CREDIT_REQUEST
      ? 'Request approved and credits added'
      : 'Request approved';

  const handleRequestUpdate = useCallback(
    (message: string, variant: 'success' | 'info') => {
      handleCloseDialog();
      refetchRequests();
      refetchNotifications();
      enqueueSnackbar(message, { variant });
    },
    [refetchRequests, handleCloseDialog, refetchNotifications]
  );

  const handleApproveRequest = useCallback(async () => {
    if (!selectedRequest || !currentUser || !currentUser.id) return;

    await updateUserRequest(selectedRequest.id, {
      type: selectedRequest.type,
      requesterId: selectedRequest.requesterId,
      status: UserRequestStatus.APPROVED,
      responderId: currentUser.id,
    });

    handleRequestUpdate(approvedMessage, 'success');
  }, [approvedMessage, currentUser, selectedRequest, handleRequestUpdate]);

  const handleDenyRequest = useCallback(async () => {
    if (!selectedRequest || !currentUser || !currentUser.id) return;

    await updateUserRequest(selectedRequest.id, {
      type: selectedRequest.type,
      requesterId: selectedRequest.requesterId,
      status: UserRequestStatus.DENIED,
      responderId: currentUser.id,
    });

    handleRequestUpdate('Request denied', 'info');
  }, [currentUser, selectedRequest, handleRequestUpdate]);

  return (
    <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle>Request Details</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              Requester Information
            </Typography>
            <Typography variant="body1">{selectedRequest?.requesterEmail || '-'}</Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedRequest?.requesterPreferredName || ''}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              Type
            </Typography>
            <Typography variant="body1">
              {selectedRequest ? typeLabels[selectedRequest.type as UserRequestType] : '-'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              Status
            </Typography>
            <Box mt={0.5}>{selectedRequest && renderStatusIcon(selectedRequest.status)}</Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              Reason for Request
            </Typography>
            <Typography variant="body1">{selectedRequest?.reasonForRequest || '-'}</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          onClick={handleApproveRequest}
        >
          Approve
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<Cancel />}
          onClick={handleDenyRequest}
        >
          Deny
        </Button>
      </DialogActions>
    </Dialog>
  );
};
